/**
 * Password Validation Utilities
 *
 * The **policy** (what counts as strong enough) is the shared user-tier definition
 * in `@butter/library/shared` — used identically by the API and every app. This
 * module re-exports that gate and adds the web-only pieces: the visual strength
 * meter and the confirm-field match check. (Members use the user tier; admin
 * surfaces live in admin-portal and use the admin tier.)
 *
 * @module lib/utils/passwordValidation
 */

import {
  validateUserPassword,
  isStrongUserPassword,
  USER_PASSWORD_MIN_LENGTH,
  USER_PASSWORD_REQUIREMENT_TEXT,
  type PasswordValidationResult,
} from '@butter/library/shared';

// Shared user-tier gate — re-exported for the web's member forms.
export { validateUserPassword, isStrongUserPassword, USER_PASSWORD_REQUIREMENT_TEXT };
export type { PasswordValidationResult };

/** Password strength levels (visual meter only — not the pass/fail gate). */
export type PasswordStrength = 'weak' | 'medium' | 'strong';

/** Password strength result for the visual indicator. */
export interface PasswordStrengthResult {
  strength: PasswordStrength;
  score: number;
  message: string;
}

/**
 * Visual strength estimate for the meter — rewards character variety beyond the
 * minimum policy bar (which is intentionally lenient). Purely cosmetic; the
 * pass/fail decision is {@link validateUserPassword} / {@link isStrongUserPassword}.
 */
export function getPasswordStrength(password: string): PasswordStrengthResult {
  if (!password) {
    return { strength: 'weak', score: 0, message: '' };
  }
  if (password.length < USER_PASSWORD_MIN_LENGTH) {
    return { strength: 'weak', score: 0, message: `Too short (minimum ${USER_PASSWORD_MIN_LENGTH} characters)` };
  }

  let score = 0;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return { strength: 'weak', score, message: 'Add an uppercase letter or a symbol' };
  if (score <= 2) return { strength: 'medium', score, message: 'Good password' };
  return { strength: 'strong', score, message: 'Strong password' };
}

/** Confirm-field check: returns an error message if the two passwords differ, else null. */
export function validatePasswordMatch(password: string, confirmPassword: string): string | null {
  return password !== confirmPassword ? 'Passwords do not match' : null;
}

/** Human-readable requirement(s) for form hints. */
export function getPasswordRequirements(): string[] {
  return [USER_PASSWORD_REQUIREMENT_TEXT];
}
